// Dynamic breadcrumb tail ([NAV-BREADCRUMB-TAIL]).
//
// The typed `route.meta.breadcrumb.tail` is a PURE function of the route, so it
// can only produce labels derivable from route params. Some leaves show FETCHED
// data instead - a meeting title, a COT round name, a subscription club name.
// Those views publish their leaf crumb(s) here; `AppBreadcrumbs` prefers this
// dynamic tail over the static meta tail for the matching route.
//
// The entry is keyed by `route.path` so a value published by an unmounting view
// can never leak onto the next page: after navigation the stored path no longer
// matches the active route and is ignored (and overwritten when the new view
// publishes its own).

import { onScopeDispose, ref, watch, type Ref } from "vue";
import { useRoute } from "vue-router";
import type { BreadcrumbItem } from "./breadcrumbs";

const dynamicTail = ref<{ path: string; items: BreadcrumbItem[] } | null>(null);

/**
 * Publish the current page's leaf crumb(s), reactively. Call from a view whose
 * breadcrumb leaf depends on fetched data; pass a ref/computed that yields the
 * items (or `null`/`[]` while loading, to fall back to the static meta tail).
 */
export function useBreadcrumbTail(items: Ref<BreadcrumbItem[] | null | undefined>): void {
  const route = useRoute();
  watch(
    items,
    (value) => {
      dynamicTail.value = value && value.length ? { path: route.path, items: value } : null;
    },
    { immediate: true },
  );
  onScopeDispose(() => {
    if (dynamicTail.value?.path === route.path) dynamicTail.value = null;
  });
}

/** The dynamic tail for `path`, or `null` if none was published for it. */
export function getDynamicBreadcrumbTail(path: string): BreadcrumbItem[] | null {
  return dynamicTail.value?.path === path ? dynamicTail.value.items : null;
}
